import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getEnv } from '../utils/env';

interface StoreHostnameState {
  customHostname: string | null;
  setCustomHostname: (hostname: string) => void;
  clearCustomHostname: () => void;
  getHostname: () => string;
}

/**
 * Zustand store for managing store hostname with localStorage persistence.
 *
 * Priority order:
 * 1. Custom hostname (set via setCustomHostname, persisted in localStorage)
 * 2. VITE_STORE_HOST_NAME environment variable
 * 3. window.location.host (fallback)
 *
 * @example
 * ```typescript
 * import { useStoreHostnameStore } from './stores/useStoreHostnameStore';
 *
 * function MyComponent() {
 *   const hostname = useStoreHostnameStore((state) => state.getHostname());
 *   const setCustomHostname = useStoreHostnameStore((state) => state.setCustomHostname);
 *
 *   // Get current hostname
 *   console.log(hostname);
 *
 *   // Set custom hostname (persists in localStorage)
 *   setCustomHostname('mystore.com');
 * }
 * ```
 */
export const useStoreHostnameStore = create<StoreHostnameState>()(
  persist(
    (set, get) => ({
      customHostname: null,

      setCustomHostname: (hostname: string) => {
        if (
          !hostname ||
          typeof hostname !== 'string' ||
          hostname.trim().length === 0
        ) {
          throw new Error('Invalid hostname: must be a non-empty string');
        }
        set({ customHostname: hostname.trim() });
      },

      clearCustomHostname: () => {
        set({ customHostname: null });
      },

      getHostname: () => {
        const state = get();

        // Priority 1: Custom hostname from store
        if (state.customHostname) {
          return state.customHostname;
        }

        // Priority 2: Environment variable
        const envHostname = getEnv('STORE_HOST_NAME');
        if (
          envHostname &&
          typeof envHostname === 'string' &&
          envHostname.trim().length > 0
        ) {
          return envHostname.trim();
        }

        // Priority 3: window.location.host
        return window.location.host;
      },
    }),
    {
      name: 'superbar-store-hostname',
      partialize: (state) => ({ customHostname: state.customHostname }),
    }
  )
);
